import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { T, t } from '@sm/intl';
import { Chip } from '@wds/chip';
import { Typography } from '@wds/typography';
import { createUseStyles } from 'react-jss';
import { Box } from '@wds/box';
import { IconPlus } from '@wds/icons';
import { useGeneral, useThemes, useThemesReuse, useThemesReuseActions } from '../../../Context';
import { COLD_START_THEMES_TO_SHOW } from '../../../constants';
import LinkStyleButton from '../../../shared/LinkStyleButton';
import FiveHundredError from '../../../shared/FiveHundredError';
import { getOecPreviouslyUsedTags } from '../../../graph/fetchPrevUsedThemes';
import BadgesSkeleton from './BadgesSkeleton';
export const COPY = {
  prevUsedHeader: {
    id: 'OpenEndedCoding.AddThemeModalColdStart.prevUsedHeader',
    defaultMessage: 'Previously-used themes',
    description: '[Type: Header][Vis: med] - Create Theme Modal themes previously used'
  },
  viewMoreButton: {
    id: 'OpenEndedCoding.AddThemeModalColdStart.viewMoreButton',
    defaultMessage: 'View more',
    description: '[Type: Header][Vis: med] - Create Theme Modal themes view more'
  },
  error: {
    id: 'OpenEndedCoding.AddThemeModalColdStart.error',
    defaultMessage: "We couldn't load your themes",
    description: '[Type: Header][Vis: med] - Create Theme Modal themes error state'
  },
  iconPlus: {
    id: 'OpenEndedCoding.ResponseCard.iconPlus',
    defaultMessage: 'Plus icon',
    description: '[Type: Label][Vis: low] - plus icon'
  }
};
const useStyles = createUseStyles(() => ({
  badges: {
    display: 'inline-flex',
    flexWrap: 'wrap'
  }
}));
const PreviouslyUsedThemes = () => {
  const classes = useStyles();
  const {
    previouslyUsedThemes,
    selectedReusedThemes
  } = useThemesReuse();
  const {
    themes
  } = useThemes();
  const {
    setPreviouslyUsedThemes
  } = useThemesReuseActions();
  const {
    surveyId,
    questionId
  } = useGeneral();
  const {
    addReusedTheme,
    removePreviouslyUsedTheme
  } = useThemesReuseActions();
  const [themesToShow, setThemesToShow] = useState(COLD_START_THEMES_TO_SHOW);
  const [error, setError] = useState(false);
  // ensure the user can't add already added themes
  const currentThemes = themes.map(({
    theme
  }) => theme);
  const prevUsedThemesAvailable = previouslyUsedThemes.filter(prevUsed => !currentThemes.includes(prevUsed) && !selectedReusedThemes.map(s => s.name).includes(prevUsed));
  const {
    loading
  } = useQuery(getOecPreviouslyUsedTags, {
    variables: {
      surveyId,
      questionId
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: ({
      getOecPreviouslyUsedTags
    }) => {
      setPreviouslyUsedThemes(getOecPreviouslyUsedTags.map(prev => prev.name));
    },
    onError: () => {
      setError(true);
    }
  });
  const handleAddTheme = themeToAdd => {
    addReusedTheme({
      name: themeToAdd,
      suggested: true
    });
    removePreviouslyUsedTheme(themeToAdd);
  };
  const PrevUsedComponent = () => {
    if (loading) {
      return _jsx(BadgesSkeleton, {});
    }
    if (error) {
      return _jsx(FiveHundredError, {
        title: t(COPY.error)
      });
    }
    return _jsxs(_Fragment, {
      children: [_jsx(Box, {
        className: classes.badges,
        children: prevUsedThemesAvailable.slice(0, themesToShow).map(p => _jsx(Box, {
          mr: 2,
          mb: 2,
          children: _jsx(Chip, {
            icon: _jsx(IconPlus, {
              title: t(COPY.iconPlus),
              color: "link"
            }),
            variant: "toggle",
            onClick: () => handleAddTheme(p),
            children: p
          })
        }, `prev-used-theme-${p}`))
      }), themesToShow < prevUsedThemesAvailable.length && _jsx(Box, {
        children: _jsx(LinkStyleButton, {
          onClick: () => {
            setThemesToShow(themesToShow + COLD_START_THEMES_TO_SHOW);
          },
          children: _jsx(T, {
            desc: COPY.viewMoreButton
          })
        })
      })]
    });
  };
  return _jsxs(_Fragment, {
    children: [(prevUsedThemesAvailable.length > 0 || loading) && _jsx(Box, {
      mb: 2,
      children: _jsx(Typography, {
        variant: "body",
        weight: "medium",
        children: _jsx(T, {
          desc: COPY.prevUsedHeader
        })
      })
    }), PrevUsedComponent()]
  });
};
export default PreviouslyUsedThemes;